/**
 * ICE Server Configuration for WebRTC
 * 
 * This module provides the ICE server configuration from environment variables.
 * Used by all WebRTC peer connections in the student classroom app.
 * 
 * Environment Variables:
 * - REACT_APP_STUN_URL: STUN server URL (e.g., stun:turn.ropera-edu.com:3478)
 * - REACT_APP_TURN_URL: TURN server host:port (optional, e.g., turn.ropera-edu.com:3478)
 * - REACT_APP_TURN_USERNAME: TURN server username (required when TURN_URL is set)
 * - REACT_APP_TURN_CREDENTIAL: TURN server credential/password (required when TURN_URL is set)
 */

// Get environment variables with safe fallbacks.
const STUN_URL = process.env.REACT_APP_STUN_URL || 'stun:turn.ropera-edu.com:3478';
const TURN_URL = process.env.REACT_APP_TURN_URL;
const TURN_USERNAME = process.env.REACT_APP_TURN_USERNAME;
const TURN_CREDENTIAL = process.env.REACT_APP_TURN_CREDENTIAL;
const ICE_TRANSPORT_POLICY = process.env.REACT_APP_WEBRTC_ICE_TRANSPORT_POLICY || 'all';
const VALID_ICE_POLICIES = new Set(['all', 'relay']);

const buildIceServers = () => {
    const iceServers = STUN_URL ? [{ urls: STUN_URL }] : [];

    if (TURN_URL && TURN_USERNAME && TURN_CREDENTIAL) {
        iceServers.push(
            {
                urls: `turn:${TURN_URL}?transport=udp`,
                username: TURN_USERNAME,
                credential: TURN_CREDENTIAL
            },
            {
                urls: `turn:${TURN_URL}?transport=tcp`,
                username: TURN_USERNAME,
                credential: TURN_CREDENTIAL
            }
        );
    }

    return iceServers;
};

/**
 * Get the full ICE server configuration for WebRTC peer connections
 * @returns {Object} RTCConfiguration object with iceServers array
 */
export const getIceConfig = () => {
    const config = {
        iceServers: buildIceServers()
    };

    if (VALID_ICE_POLICIES.has(ICE_TRANSPORT_POLICY)) {
        config.iceTransportPolicy = ICE_TRANSPORT_POLICY;
    }

    return config;
};

/**
 * Get ICE servers array directly (for SimplePeer config)
 * @returns {Array} Array of ICE server objects
 */
export const getIceServers = () => buildIceServers();

/**
 * Get SimplePeer compatible config object
 * @param {boolean} initiator - Whether this peer is the initiator
 * @param {MediaStream} stream - Optional media stream
 * @returns {Object} SimplePeer configuration object
 */
export const getSimplePeerConfig = (initiator = false, stream = null) => {
    const config = {
        initiator,
        trickle: true,
        config: getIceConfig()
    };
    
    if (stream) {
        config.stream = stream;
    }
    
    return config;
};

// Export individual values for cases where they're needed separately
export const ICE_CONFIG = {
    STUN_URL,
    TURN_URL,
    TURN_USERNAME,
    TURN_CREDENTIAL,
    ICE_TRANSPORT_POLICY
};

export default getIceConfig;
